/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Paper,
  Grid,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  UserOutlined,
  CarOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  HomeOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
  AlertOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import {
  Person,
  DirectionsCar,
  ContactEmergency,
  Security,
  Edit,
  AccountCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useUser } from "../../../UserContext";
import api from "../../../axiosConfig";
import {
  UnifiedContainer,
  UnifiedLogo,
  UnifiedCardLight,
  CenteredLayout,
  colors,
  fadeIn
} from "../../../theme/SharedDesign";
import { styled } from "@mui/material/styles";
import logo from "../../../Assets/img/logo.webp";

// Función auxiliar para dar formato a fechas
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return format(new Date(dateString), "dd MMMM yyyy", { locale: es });
  } catch (error) {
    return "";
  }
};

// Header del perfil con estilo claro
const ProfileHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  background: 'rgba(10, 46, 92, 0.1)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  position: 'relative',
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  borderBottom: '1px solid rgba(10, 46, 92, 0.2)',
}));

// Avatar con estilo claro
const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 70,
  height: 70,
  background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
  color: '#ffffff',
  border: '3px solid rgba(255, 255, 255, 0.5)',
  fontSize: 28,
  fontWeight: 600,
  boxShadow: '0 0 20px rgba(10, 46, 92, 0.4)',
}));

// Botón de editar
const EditButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'rgba(10, 46, 92, 0.1)',
  color: '#0a2e5c',
  width: 40,
  height: 40,
  transition: 'all 0.3s ease',
  border: '1px solid rgba(10, 46, 92, 0.3)',
  
  '&:hover': {
    background: 'rgba(10, 46, 92, 0.2)',
    transform: 'scale(1.1)',
    boxShadow: '0 4px 15px rgba(10, 46, 92, 0.3)',
  },
}));

// Pestañas
const TabButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  flex: 1,
  textAlign: "center",
  padding: theme.spacing(1.5, 1),
  cursor: "pointer",
  background: isActive ? 'rgba(10, 46, 92, 0.15)' : 'rgba(255, 255, 255, 0.5)',
  borderBottom: isActive ? `3px solid #0a2e5c` : "1px solid rgba(10, 46, 92, 0.2)",
  color: isActive ? '#0a2e5c' : 'rgba(10, 46, 92, 0.7)',
  fontWeight: isActive ? 700 : 600,
  fontSize: '0.9rem',
  transition: "all 0.3s ease",
  
  "&:hover": {
    background: 'rgba(10, 46, 92, 0.1)',
    color: '#0a2e5c',
  },
}));

// Contenedor de pestañas
const TabsContainer = styled(Box)(() => ({
  display: 'flex',
  background: 'rgba(255, 255, 255, 0.3)',
}));

// Campo de información
const InfoField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.7)',
  borderRadius: 12,
  border: '1px solid rgba(10, 46, 92, 0.2)',
  transition: 'all 0.3s ease',
  animation: `${fadeIn} 0.3s ease-out`,
  
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.85)',
    borderColor: 'rgba(10, 46, 92, 0.4)',
    boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
    transform: 'translateY(-2px)',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'rgba(10, 46, 92, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(1),
  border: '1px solid rgba(10, 46, 92, 0.3)',
  
  '& .anticon, & .MuiSvgIcon-root': {
    color: '#0a2e5c',
    fontSize: '1.2rem',
  },
}));

// Componente para mostrar un campo de información
const InfoFieldComponent = ({ icon, label, value }) => {
  const Icon = icon;

  // No mostrar campos sin valor
  if (!value) return null;

  return (
    <InfoField>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <IconContainer>
          {Icon && <Icon />}
        </IconContainer>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="caption"
            sx={{ 
              color: '#0a2e5c',
              fontWeight: 700,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              display: 'block',
              marginBottom: 0.5,
            }}
          >
            {label}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#2c3e50',
              fontWeight: 600,
              fontSize: '0.95rem',
              lineHeight: 1.4,
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </InfoField>
  );
};

function Profile() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [fullUserData, setFullUserData] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Cargar datos completos del conductor
  useEffect(() => {
    const fetchFullUserData = async () => {
      if (user?.id) {
        setLoading(true);
        try {
          const response = await api.get(`/conductor/perfil/${user.id}`);
          setFullUserData(response.data);
        } catch (error) {
          console.error("Error al cargar datos del conductor:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchFullUserData();
  }, [user]);

  // Función para navegar al cambio de contraseña
  const handleChangePassword = () => {
    navigate("/modules/ChangePassword");
  };

  // Si no hay usuario, mostrar mensaje
  if (!user) {
    return (
      <UnifiedContainer>
        <CenteredLayout isMobile={isMobile}>
          <UnifiedCardLight sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="h5" sx={{ color: '#0a2e5c' }}>
              No se ha iniciado sesión
            </Typography>
            <Typography variant="body1" sx={{ mt: 2, color: 'rgba(10, 46, 92, 0.7)' }}>
              Por favor, inicia sesión para ver tu perfil.
            </Typography>
          </UnifiedCardLight>
        </CenteredLayout>
      </UnifiedContainer>
    );
  }

  return (
    <UnifiedContainer>
      <CenteredLayout isMobile={isMobile} maxWidth={600}>
        <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
        
        <UnifiedCardLight elevation={0} sx={{ width: '100%', p: 0, overflow: 'hidden' }}>
          {/* Header del perfil */}
          <ProfileHeader>
            <EditButton onClick={handleChangePassword}>
              <Edit sx={{ fontSize: '1.2rem' }} />
            </EditButton>
            
            <ProfileAvatar>
              <AccountCircle sx={{ fontSize: 32 }} />
            </ProfileAvatar>

            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700, 
                  marginBottom: 0.5,
                  fontSize: '1.2rem',
                  color: '#0a2e5c',
                }}
              >
                {user.nombre || "Conductor De Pruebas"}
              </Typography>

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  icon={<IdcardOutlined />}
                  label={user.numero_documento || fullUserData?.numero_documento || ""}
                  size="small"
                  sx={{
                    background: 'rgba(10, 46, 92, 0.1)',
                    color: '#0a2e5c',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 28,
                    border: '1px solid rgba(10, 46, 92, 0.3)',
                    '& .anticon': {
                      color: '#0a2e5c',
                      fontSize: '0.9rem',
                    },
                  }}
                />
                <Chip
                  icon={<CarOutlined />}
                  label={
                    fullUserData?.categoria_licencia
                      ? `Lic. ${fullUserData.categoria_licencia}`
                      : "Lic. A1"
                  }
                  size="small"
                  sx={{
                    background: 'rgba(10, 46, 92, 0.1)',
                    color: '#0a2e5c',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 28,
                    border: '1px solid rgba(10, 46, 92, 0.3)',
                    '& .anticon': {
                      color: '#0a2e5c',
                      fontSize: '0.9rem',
                    },
                  }}
                />
              </Box>
            </Box>
          </ProfileHeader>

          {/* Pestañas */}
          <TabsContainer>
            <TabButton
              isActive={tabValue === 0}
              onClick={() => setTabValue(0)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                <Person sx={{ fontSize: '1rem' }} />
                <span>Personal</span>
              </Box>
            </TabButton>
            
            <TabButton
              isActive={tabValue === 1}
              onClick={() => setTabValue(1)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                <DirectionsCar sx={{ fontSize: '1rem' }} />
                <span>Vehículo</span>
              </Box>
            </TabButton>
            
            <TabButton
              isActive={tabValue === 2}
              onClick={() => setTabValue(2)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexDirection: 'column' }}>
                <ContactEmergency sx={{ fontSize: '1rem' }} />
                <span>Emergencia</span>
              </Box>
            </TabButton>
          </TabsContainer>

          {/* Contenido */}
          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ 
                py: 4, 
                display: "flex", 
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: "center",
                gap: 2,
              }}>
                <CircularProgress 
                  size={50}
                  sx={{
                    color: '#0a2e5c',
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round',
                    },
                  }}
                />
                <Typography sx={{ color: '#0a2e5c', fontWeight: 600 }}>
                  Cargando perfil...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Pestaña de Información Personal */}
                {tabValue === 0 && (
                  <Fade in={tabValue === 0}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <InfoFieldComponent
                          icon={MailOutlined}
                          label="Correo Electrónico"
                          value={user.correo_electronico || fullUserData?.correo}
                        />
                        <InfoFieldComponent
                          icon={PhoneOutlined}
                          label="Teléfono"
                          value={fullUserData?.telefono}
                        />
                        <InfoFieldComponent
                          icon={HomeOutlined}
                          label="Dirección"
                          value={fullUserData?.direccion}
                        />
                        {(fullUserData?.ciudad || fullUserData?.departamento) && (
                          <InfoFieldComponent
                            icon={HomeOutlined}
                            label="Ciudad"
                            value={`${fullUserData.ciudad || ""} ${
                              fullUserData.departamento
                                ? `, ${fullUserData.departamento}`
                                : ""
                            }`}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {fullUserData?.fecha_nacimiento && (
                          <InfoFieldComponent
                            icon={CalendarOutlined}
                            label="Fecha de Nacimiento"
                            value={formatDate(fullUserData.fecha_nacimiento)}
                          />
                        )}
                        <InfoFieldComponent
                          icon={SafetyCertificateOutlined}
                          label="Licencia de Conducción"
                          value={`${fullUserData?.licencia_conduccion || ""} ${
                            fullUserData?.categoria_licencia
                              ? `(${fullUserData.categoria_licencia})`
                              : ""
                          }`}
                        />
                        {fullUserData?.fecha_vencimiento_licencia && (
                          <InfoFieldComponent
                            icon={CalendarOutlined}
                            label="Vencimiento Licencia"
                            value={formatDate(
                              fullUserData.fecha_vencimiento_licencia
                            )}
                          />
                        )}
                        <InfoFieldComponent
                          icon={AlertOutlined}
                          label="Grupo Sanguíneo"
                          value={fullUserData?.grupo_sanguineo}
                        />
                      </Grid>
                    </Grid>
                  </Fade>
                )}

                {/* Pestaña de Vehículo */}
                {tabValue === 1 && (
                  <Fade in={tabValue === 1}>
                    <Box>
                      {!user.vehiculoPrincipal ||
                      !user.vehiculoPrincipal.Vehiculo ? (
                        <Box 
                          sx={{ 
                            py: 6, 
                            textAlign: "center",
                            background: 'rgba(10, 46, 92, 0.05)',
                            borderRadius: 3,
                            border: '1px solid rgba(10, 46, 92, 0.2)',
                          }}
                        >
                          <DirectionsCar 
                            sx={{ 
                              fontSize: '4rem', 
                              color: 'rgba(10, 46, 92, 0.6)',
                              mb: 2,
                            }} 
                          />
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: '#0a2e5c',
                              fontWeight: 600,
                            }}
                          >
                            No tienes un vehículo asignado actualmente.
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <InfoField>
                              <IconContainer>
                                <CarOutlined />
                              </IconContainer>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{ 
                                    color: '#0a2e5c',
                                    fontWeight: 600,
                                    marginBottom: 1,
                                    fontSize: '0.9rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Placa del Vehículo
                                </Typography>
                                <Typography
                                  variant="h3"
                                  sx={{
                                    color: '#0a2e5c',
                                    fontWeight: 800,
                                    letterSpacing: '2px',
                                  }}
                                >
                                  {user.vehiculoPrincipal.Vehiculo.placa || "—"}
                                </Typography>
                              </Box>
                            </InfoField>
                          </Grid>
                          
                          <Grid item xs={12} md={6}>
                            <InfoField>
                              <IconContainer>
                                <Security />
                              </IconContainer>
                              <Box sx={{ flex: 1, textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{ 
                                    color: '#0a2e5c',
                                    fontWeight: 600,
                                    marginBottom: 1,
                                    fontSize: '0.9rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                  }}
                                >
                                  Código del Vehículo
                                </Typography>
                                <Typography
                                  variant="h3"
                                  sx={{
                                    color: '#0a2e5c',
                                    fontWeight: 800,
                                    letterSpacing: '2px',
                                  }}
                                >
                                  {user.vehiculoPrincipal.Vehiculo.codigo || "—"}
                                </Typography>
                              </Box>
                            </InfoField>
                          </Grid>
                          
                          {user.vehiculoPrincipal.Vehiculo.estado === "ACTIVO" && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 2,
                                  padding: 2,
                                  background: 'rgba(76, 175, 80, 0.1)',
                                  border: '2px solid rgba(76, 175, 80, 0.3)',
                                  borderRadius: 3,
                                }}
                              >
                                <CheckCircleOutlined
                                  style={{ 
                                    color: "#4caf50", 
                                    fontSize: '1.5rem',
                                  }}
                                />
                                <Typography 
                                  sx={{ 
                                    color: "#4caf50", 
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                  }}
                                >
                                  Vehículo Activo y Operativo
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      )}
                    </Box>
                  </Fade>
                )}

                {/* Pestaña de Contacto de Emergencia */}
                {tabValue === 2 && (
                  <Fade in={tabValue === 2}>
                    <Box>
                      {!fullUserData ? (
                        <Box 
                          sx={{ 
                            py: 6, 
                            textAlign: "center",
                            background: 'rgba(10, 46, 92, 0.05)',
                            borderRadius: 3,
                            border: '1px solid rgba(10, 46, 92, 0.2)',
                          }}
                        >
                          <ContactEmergency 
                            sx={{ 
                              fontSize: '4rem', 
                              color: 'rgba(10, 46, 92, 0.6)',
                              mb: 2,
                            }} 
                          />
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: '#0a2e5c',
                              fontWeight: 600,
                            }}
                          >
                            Sin información de contacto de emergencia.
                          </Typography>
                        </Box>
                      ) : (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <InfoFieldComponent
                              icon={UserOutlined}
                              label="Nombre Contacto"
                              value={fullUserData.nombre_emergencia}
                            />
                            <InfoFieldComponent
                              icon={TeamOutlined}
                              label="Parentesco"
                              value={fullUserData.parentesco_emergencia}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <InfoFieldComponent
                              icon={PhoneOutlined}
                              label="Teléfono Emergencia"
                              value={fullUserData.numero_emergencia}
                            />
                            <InfoFieldComponent
                              icon={SafetyCertificateOutlined}
                              label="ARL"
                              value={fullUserData.arl}
                            />
                            {fullUserData.fecha_vigencia_arl && (
                              <InfoFieldComponent
                                icon={CalendarOutlined}
                                label="Vigencia ARL"
                                value={formatDate(fullUserData.fecha_vigencia_arl)}
                              />
                            )}
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Fade>
                )}
              </>
            )}
          </Box>
        </UnifiedCardLight>
      </CenteredLayout>
    </UnifiedContainer>
  );
}

export default Profile;